import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../apiBase';
import type { InspectorEnums } from './ElementInspectorFields';
import { defaultActOrder, defaultBeatOrder, enumDisplayLabel } from '../utils/enumTranslator';
import { clampZoom, fitViewport } from '../canvasViewport';

type Locale = 'zh' | 'en';

interface DramaCanvasProps {
  locale?: Locale;
  enumOptions?: InspectorEnums;
  onElementCreate: (type: string, position: { x: number; y: number }) => void;
  onElementCreateFromDraft?: (type: string, draft: Record<string, any>) => void;
  onConnectionCreate: (
    sourceId: string,
    targetId: string,
    relationshipType: string,
    options?: { semanticFamily?: string }
  ) => void;
  onSetAct: (elementId: string, act: string) => void;
  onSetBeat: (elementId: string, beat: string) => void;
  onElementPatch?: (
    elementId: string,
    patch: Record<string, any>,
    statusMessage?: string
  ) => void | Promise<void>;
  onElementEdit?: (element: any) => void;
  onElementDelete?: (elementId: string) => void;
  onElementPositionUpdate?: (elementId: string, position: { x: number; y: number }) => void;
  onElementSelect?: (elementId: string) => void;
  selectedId?: string | null;
  dramaElements: any[];
  connections: any[];
}

const normalizeEpisodeLabel = (value: any) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }
  const match = raw.match(/(\d+)/);
  if (match) {
    return `第${Number(match[1])}集`;
  }
  return raw;
};

const episodeSortValue = (value: string) => {
  const match = String(value || '').match(/(\d+)/);
  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }
  return Number(match[1]);
};

const CANVAS_COPY = {
  zh: {
    unknownError: '未知错误',
    deviceMissing: '装置不存在',
    arrayInput: '输入数组，用逗号分隔',
    arrayOrJsonInput: '输入数组，用逗号分隔或 JSON 格式',
    character: '人物',
    event: '事件',
    scene: '情境',
    theme: '主题',
    worldview: '世界观',
    dialogue: '对话',
    device: '装置',
    plotDevice: '情节装置',
    arcMilestone: '角色弧里程碑',
    unnamed: '未命名',
    collapse: '收起',
    expand: '展开',
    editDetails: '编辑详情',
    relationCount: '关联',
    typeBadge: '类型',
    emptyEventDescription: '事件描述为空，无法生成图片',
    imageGenerationFailed: '生成图片失败',
    generatingImage: '🖼️ 生成中...',
    generateImage: '🖼️ 生成图片',
    delete: '删除',
    intensity: '强度',
    editConflict: '编辑冲突',
    conflictAxesPrompt: '输入冲突类型（逗号分隔）：man_vs_self,man_vs_man,man_vs_society,man_vs_nature,man_vs_supernatural,man_vs_tech',
    conflictIntensityPrompt: '强度(1-5)：',
    conflictRolesPrompt: '参与角色 ID（逗号分隔，可为空）：',
    conflictPairsHint: '格式: source:target:intensity:note; 多对用分号分隔, 如 c1:c2:4:公开指责;c3:c4:3',
    conflictPairsPrompt: '人与人冲突对（可为空）',
    conflictDrafted: '冲突已加入草稿',
    conflictUpdateAction: '更新冲突信息',
    refreshDevices: '刷新装置',
    addDevice: '新增装置',
    deviceEditor: '装置编辑',
    deviceTitlePrompt: '装置标题',
    deviceTypePrompt: '装置类型（从后端类型中选择）：',
    deviceTypeFallbackPrompt: '装置类型（如 Foreshadowing/Clue/Reversal/...）',
    addDeviceAction: '新增装置',
    loadDeviceTypesAction: '加载装置类型',
    refreshDevicesAction: '刷新装置',
    refreshOrCreateDevice: '请先刷新装置或新增装置',
    deviceTypeFilterPrompt: '筛选装置类型（可空，示例：Foreshadowing/Clue/...）',
    noMatchingDevices: '无匹配装置',
    setupSelectionPrompt: '选择要标记为本场 Setup 的装置 ID（可从刷新结果中查看）',
    payoffSelectionPrompt: '选择要标记为本场 Payoff 的装置 ID',
    setupDrafted: '装置 Setup 已加入草稿',
    payoffDrafted: '装置 Payoff 已加入草稿',
    markSetupAction: '标记 Setup',
    markPayoffAction: '标记 Payoff',
    markCurrentSceneSetup: '标记本场为 Setup',
    markCurrentScenePayoff: '标记本场为 Payoff',
    typeLabel: '类型',
    all: '全部',
    deviceLabel: '装置',
    evidence: '证据',
    chooseDevice: '请选择装置',
    unavailableDeviceTypeInfo: '无法获取装置类型信息',
    editDeviceTitle: '编辑装置',
    cancel: '取消',
    save: '保存',
    updateFailed: '更新失败',
    deviceEditedDrafted: '装置修改已加入草稿',
    newDevice: '新增装置',
    title: '标题',
    enterTitleAndType: '请输入标题并选择类型',
    createDeviceAction: '创建装置',
    create: '创建',
    noBeat: '无节拍',
    catalystEvent: '触发事件',
    enterSecondAct: '进入第二幕',
    bStoryFull: 'B故事',
    funAndGamesFull: '趣味与游戏',
    badGuysCloseInFull: '反派逼近',
    allIsLostFull: '一切尽失',
    darkNightFull: '灵魂黑夜',
    enterThirdAct: '进入第三幕',
    finalImage: '最终画面',
    eventAssignedDrafted: '事件归属已加入草稿',
    unboundScene: '未绑定场景',
    sceneFallback: '场景',
    addMilestone: '新增里程碑',
    milestoneTypePrompt: '里程碑类型（如 Need/Want/Lie/Truth/...）',
    bindModePrompt: '绑定到（输入 event/beat/none）：',
    beatPrompt: '请输入节拍（如 MIDPOINT/ALL_IS_LOST/...）',
    eventIdPrompt: '请输入目标事件在画布中的 ID（可从列表面板复制）',
    milestoneDrafted: '角色里程碑已加入草稿',
    createMilestoneAction: '创建角色里程碑',
    binding: '绑定',
    none: '无',
    eventPrefix: '事件',
    beatPrefix: '节拍',
    renamePrompt: '请输入新名称:',
    renameLog: '更新元素名称',
    connectionModeActive: '连接模式已激活',
    connectionModeHint: '点击第一个元素，然后点击第二个元素',
    cancelConnection: '取消连接',
    connectionMode: '连接模式',
    startConnection: '开始连接',
    connectionHelp: '连线帮助',
    connectionRulesTitle: 'Canvas 连线规则',
    supportedConnectionTypes: '支持的连线类型：',
    instructionsTitle: '操作说明：',
    notesTitle: '注意事项：',
    instructions: [
      '点击“开始连接”按钮进入连接模式',
      '点击第一个元素作为连接起点',
      '点击第二个元素作为连接终点',
      '输入关系标签；如需可以自定义',
      '系统会自动验证连线是否被允许',
    ],
    notes: [
      '某些复杂绑定仍需要使用专用面板（如装置编辑面板）',
      '若输入自定义关系，系统会再询问语义族',
      '不支持的元素连线会给出替代操作建议',
    ],
    unsupportedConnection: '不支持的连线类型',
    suggestion: '建议',
    deviceBindingSuggestion: '这类绑定请使用装置编辑面板。',
    eventBindingSuggestion: '归属型关系请使用事件所属场景下拉框。',
    unsupportedPairSuggestion: '当前画布不支持这组元素直接连线。',
    relationshipPrompt: '请输入关系标签',
    customLabelHint: '也可以输入自定义标签。',
    semanticFamilyPrompt: '请输入语义族',
  },
  en: {
    unknownError: 'Unknown error',
    deviceMissing: 'Device not found',
    arrayInput: 'Enter comma-separated values',
    arrayOrJsonInput: 'Enter a comma-separated list or JSON array',
    character: 'Character',
    event: 'Event',
    scene: 'Scene',
    theme: 'Theme',
    worldview: 'Worldview',
    dialogue: 'Dialogue',
    device: 'Device',
    plotDevice: 'Plot device',
    arcMilestone: 'Arc milestone',
    unnamed: 'Untitled',
    collapse: 'Collapse',
    expand: 'Expand',
    editDetails: 'Edit details',
    relationCount: 'Relations',
    typeBadge: 'Type',
    emptyEventDescription: 'The event description is empty, so no image can be generated.',
    imageGenerationFailed: 'Image generation failed',
    generatingImage: '🖼️ Generating...',
    generateImage: '🖼️ Generate image',
    delete: 'Delete',
    intensity: 'Intensity',
    editConflict: 'Edit conflict',
    conflictAxesPrompt: 'Enter conflict types (comma-separated): man_vs_self,man_vs_man,man_vs_society,man_vs_nature,man_vs_supernatural,man_vs_tech',
    conflictIntensityPrompt: 'Intensity (1-5):',
    conflictRolesPrompt: 'Participant character IDs (comma-separated, optional):',
    conflictPairsHint: 'Format: source:target:intensity:note; separate multiple pairs with semicolons',
    conflictPairsPrompt: 'Character-vs-character conflict pairs (optional)',
    conflictDrafted: 'Conflict changes were added to the draft',
    conflictUpdateAction: 'Update conflict details',
    refreshDevices: 'Refresh devices',
    addDevice: 'Add device',
    deviceEditor: 'Device editor',
    deviceTitlePrompt: 'Device title',
    deviceTypePrompt: 'Device type (choose from backend types):',
    deviceTypeFallbackPrompt: 'Device type (for example Foreshadowing/Clue/Reversal/...)',
    addDeviceAction: 'Create device',
    loadDeviceTypesAction: 'Load device types',
    refreshDevicesAction: 'Refresh devices',
    refreshOrCreateDevice: 'Refresh device types or create a new device first',
    deviceTypeFilterPrompt: 'Filter by device type (optional, example: Foreshadowing/Clue/...)',
    noMatchingDevices: 'No matching devices',
    setupSelectionPrompt: 'Choose the device ID to mark as Setup for this scene',
    payoffSelectionPrompt: 'Choose the device ID to mark as Payoff for this scene',
    setupDrafted: 'Device Setup was added to the draft',
    payoffDrafted: 'Device Payoff was added to the draft',
    markSetupAction: 'Mark Setup',
    markPayoffAction: 'Mark Payoff',
    markCurrentSceneSetup: 'Mark this scene as Setup',
    markCurrentScenePayoff: 'Mark this scene as Payoff',
    typeLabel: 'Type',
    all: 'All',
    deviceLabel: 'Device',
    evidence: 'Evidence',
    chooseDevice: 'Choose a device first',
    unavailableDeviceTypeInfo: 'Failed to load device type details',
    editDeviceTitle: 'Edit device',
    cancel: 'Cancel',
    save: 'Save',
    updateFailed: 'Update failed',
    deviceEditedDrafted: 'Device changes were added to the draft',
    newDevice: 'New device',
    title: 'Title',
    enterTitleAndType: 'Enter a title and choose a type',
    createDeviceAction: 'Create device',
    create: 'Create',
    noBeat: 'No beat',
    catalystEvent: 'Catalyst',
    enterSecondAct: 'Break into Two',
    bStoryFull: 'B Story',
    funAndGamesFull: 'Fun and Games',
    badGuysCloseInFull: 'Bad Guys Close In',
    allIsLostFull: 'All Is Lost',
    darkNightFull: 'Dark Night of the Soul',
    enterThirdAct: 'Break into Three',
    finalImage: 'Final Image',
    eventAssignedDrafted: 'Event assignment was added to the draft',
    unboundScene: 'No scene binding',
    sceneFallback: 'Scene',
    addMilestone: 'Add milestone',
    milestoneTypePrompt: 'Milestone type (for example Need/Want/Lie/Truth/...)',
    bindModePrompt: 'Bind to (enter event/beat/none):',
    beatPrompt: 'Enter a beat (for example MIDPOINT/ALL_IS_LOST/...)',
    eventIdPrompt: 'Enter the target event ID from the canvas',
    milestoneDrafted: 'Character milestone was added to the draft',
    createMilestoneAction: 'Create character milestone',
    binding: 'Binding',
    none: 'None',
    eventPrefix: 'Event',
    beatPrefix: 'Beat',
    renamePrompt: 'Enter a new name:',
    renameLog: 'Rename element',
    connectionModeActive: 'Connection mode is active',
    connectionModeHint: 'Click the first element, then click the second element',
    cancelConnection: 'Cancel connection',
    connectionMode: 'Connection mode',
    startConnection: 'Start connection',
    connectionHelp: 'Connection help',
    connectionRulesTitle: 'Canvas connection rules',
    supportedConnectionTypes: 'Supported connection types:',
    instructionsTitle: 'Instructions:',
    notesTitle: 'Notes:',
    instructions: [
      'Click “Start connection” to enter connection mode',
      'Click the first element as the source',
      'Click the second element as the target',
      'Enter a relationship label, or type a custom one',
      'The system validates whether the connection is allowed',
    ],
    notes: [
      'Some complex bindings still belong in dedicated editors such as the device panel',
      'If you enter a custom label, the canvas will ask for a semantic family',
      'Unsupported element pairs will show a suggested alternative action',
    ],
    unsupportedConnection: 'Unsupported connection type',
    suggestion: 'Suggestion',
    deviceBindingSuggestion: 'Use the device editor for this binding.',
    eventBindingSuggestion: 'Use event-to-scene assignment for ownership links.',
    unsupportedPairSuggestion: 'This element pair is not supported on the canvas.',
    relationshipPrompt: 'Enter relationship label',
    customLabelHint: 'You can also type a custom label.',
    semanticFamilyPrompt: 'Enter semantic family',
  },
} as const;

const arr = (value: any) => (Array.isArray(value) ? value : []);
const DRAG_THRESHOLD_PX = 6;

const DramaCanvas: React.FC<DramaCanvasProps> = ({
  locale = 'zh',
  enumOptions,
  onElementCreate,
  onElementCreateFromDraft,
  onConnectionCreate,
  onSetAct,
  onSetBeat,
  onElementPatch,
  onElementEdit,
  onElementDelete,
  onElementPositionUpdate,
  onElementSelect,
  selectedId,
  dramaElements,
  connections
}) => {
  const text = CANVAS_COPY[locale];
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionSource, setConnectionSource] = useState<string | null>(null);
  const [devicesByScene, setDevicesByScene] = useState<Record<string, any[]>>({});
  const [deviceTypeHints, setDeviceTypeHints] = useState<Record<string, string[]>>({});
  const [deviceTypeSchemas, setDeviceTypeSchemas] = useState<Record<string, any>>({});
  const [showConnectionHelp, setShowConnectionHelp] = useState(false);
  const [showDeviceEditorByScene, setShowDeviceEditorByScene] = useState<Record<string, boolean>>({});
  const [selectedDeviceTypeByScene, setSelectedDeviceTypeByScene] = useState<Record<string, string>>({});
  const [selectedDeviceIdByScene, setSelectedDeviceIdByScene] = useState<Record<string, string>>({});
  const [expandedElements, setExpandedElements] = useState<Record<string, boolean>>({});
  const [draggingElement, setDraggingElement] = useState<string | null>(null);
  const [pendingDrag, setPendingDrag] = useState<{
    elementId: string;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const [hoveredElement, setHoveredElement] = useState<string | null>(null);
  const [eventImages, setEventImages] = useState<Record<string, string>>({});
  const [generatingImages, setGeneratingImages] = useState<Record<string, boolean>>({});
  const [connectionPairRules, setConnectionPairRules] = useState<Record<string, any>>({});
  const [connectionFamilyMap, setConnectionFamilyMap] = useState<Record<string, string>>({});
  const [relationshipFamilies, setRelationshipFamilies] = useState<string[]>([]);
  const [zoom, setZoom] = useState(1);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const isMobileLayout = typeof window !== 'undefined' && window.innerWidth <= 768;
  const connectionControlsBottom = isMobileLayout ? '12px' : '20px';
  
  const beatOrder = defaultBeatOrder(enumOptions);
  const actOrder = defaultActOrder(enumOptions);

  useEffect(() => {
    let cancelled = false;
    const loadConnectionRules = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/connection-rules`);
        if (cancelled) return;
        setConnectionPairRules(response.data?.pair_rules || {});
        setConnectionFamilyMap(response.data?.relationship_family_map || {});
        setRelationshipFamilies(
          Array.isArray(response.data?.semantic_families) ? response.data.semantic_families : []
        );
      } catch (_error) {
        if (!cancelled) {
          setConnectionPairRules({});
          setConnectionFamilyMap({});
          setRelationshipFamilies([]);
        }
      }
    };
    loadConnectionRules();
    return () => {
      cancelled = true;
    };
  }, []);

  const sceneElements = useMemo(
    () => dramaElements.filter((element) => element.type === 'scene'),
    [dramaElements]
  );
  const eventElements = useMemo(
    () => dramaElements.filter((element) => element.type === 'event'),
    [dramaElements]
  );
  const plotDeviceElements = useMemo(
    () => dramaElements.filter((element) => element.type === 'plot_device' || element.type === 'device'),
    [dramaElements]
  );
  const sceneNameById = useMemo(
    () =>
      new Map(
        sceneElements.map((item) => [
          String(item.id),
          String(item.name || item.data?.title || item.data?.scene_name || item.id),
        ])
      ),
    [sceneElements]
  );
  const characterNameById = useMemo(
    () =>
      new Map(
        dramaElements
          .filter((item) => item.type === 'character')
          .map((item) => [String(item.id), String(item.name || item.data?.name || item.id)])
      ),
    [dramaElements]
  );
  const sceneIdByName = useMemo(
    () =>
      new Map(
        sceneElements
          .map(
            (item): [string, string] => [
              String(item.name || item.data?.title || item.data?.scene_name || '').trim().toLowerCase(),
              String(item.id),
            ]
          )
          .filter(([key]) => Boolean(key))
      ),
    [sceneElements]
  );
  const characterIdByName = useMemo(
    () =>
      new Map(
        dramaElements
          .filter((item) => item.type === 'character')
          .map(
            (item): [string, string] => [String(item.name || item.data?.name || '').trim().toLowerCase(), String(item.id)]
          )
          .filter(([key]) => Boolean(key))
      ),
    [dramaElements]
  );
  const lanePlan = useMemo(() => {
    const scenes = dramaElements.filter((item) => item.type === 'scene');
    const events = dramaElements.filter((item) => item.type === 'event');
    const sceneEpisodeById = new Map<string, string>();
    const sceneStructureById = new Map<string, string>();

    scenes.forEach((scene) => {
      const sceneId = String(scene.id);
      const episode = normalizeEpisodeLabel(scene.data?.episode);
      if (episode) {
        sceneEpisodeById.set(sceneId, episode);
      }
      const structure = String(
        scene.data?.story_phase ?? scene.data?.phase ?? scene.data?.chapter ?? scene.data?.sequence ??
        scene.data?.act ?? scene.data?.act_type ?? scene.data?.actType ?? ''
      ).trim();
      if (structure) sceneStructureById.set(sceneId, structure);
    });

    const activeEpisodes = Array.from(new Set(sceneEpisodeById.values())).sort((left, right) => {
      return episodeSortValue(left) - episodeSortValue(right) || left.localeCompare(right);
    });

    const activeStructures = Array.from(new Set(sceneStructureById.values())).sort((left, right) => {
      const leftNumber = Number(left.match(/(\d+)/)?.[1] || Number.MAX_SAFE_INTEGER);
      const rightNumber = Number(right.match(/(\d+)/)?.[1] || Number.MAX_SAFE_INTEGER);
      return leftNumber - rightNumber || left.localeCompare(right);
    });
    const mode: 'episode' | 'structure' | 'sequence' = activeEpisodes.length > 1 ? 'episode' : activeStructures.length > 1 ? 'structure' : 'sequence';
    const lanes = mode === 'episode' ? activeEpisodes : mode === 'structure' ? activeStructures : ['场景序列'];

    const eventSceneId = new Map<string, string>();
    events.forEach((event) => {
      eventSceneId.set(String(event.id), String(event.data?.scene_id || event.data?.sceneId || ''));
    });

    const inferLane = (element: any) => {
      const type = String(element?.type || '');
      if (mode === 'episode') {
        if (type === 'scene') {
          return sceneEpisodeById.get(String(element.id)) || '';
        }
        if (type === 'event') {
          const sceneId = eventSceneId.get(String(element.id)) || '';
          return sceneEpisodeById.get(sceneId) || '';
        }
        if (type === 'dialogue') {
          const sceneId = String(element.data?.scene_id || element.data?.sceneId || '');
          return sceneEpisodeById.get(sceneId) || '';
        }
        if (type === 'plot_device' || type === 'device') {
          const linkedSceneId =
            String(
              element.data?.attached_scene_id ||
                element.data?.attachedSceneId ||
                element.data?.setup_nodes?.[0]?.scene_id ||
                element.data?.payoff_nodes?.[0]?.scene_id ||
                element.data?.links?.scenes?.[0] ||
                ''
            );
          if (linkedSceneId) {
            return sceneEpisodeById.get(linkedSceneId) || '';
          }
        }
        return '';
      }

      if (type === 'scene') {
        return sceneStructureById.get(String(element.id)) || lanes[0];
      }
      if (type === 'event') {
        const sceneId = eventSceneId.get(String(element.id)) || '';
        return sceneStructureById.get(sceneId) || String(element.data?.story_phase ?? element.data?.phase ?? element.data?.chapter ?? element.data?.sequence ?? element.data?.act ?? element.data?.act_type ?? element.data?.actType ?? '').trim() || lanes[0];
      }
      if (type === 'dialogue') {
        const sceneId = String(element.data?.scene_id || element.data?.sceneId || '');
        return sceneStructureById.get(sceneId) || lanes[0];
      }
      if (type === 'plot_device' || type === 'device') {
        const linkedSceneId =
          String(
            element.data?.attached_scene_id ||
              element.data?.attachedSceneId ||
              element.data?.setup_nodes?.[0]?.scene_id ||
              element.data?.payoff_nodes?.[0]?.scene_id ||
              element.data?.links?.scenes?.[0] ||
              ''
          );
        if (linkedSceneId) {
          return sceneStructureById.get(linkedSceneId) || lanes[0];
        }
      }
      return lanes[0];
    };

    return {
      mode,
      lanes,
      inferLane,
    };
  }, [dramaElements]);
  const displayConnections = useMemo(() => {
    const resolveSceneId = (value: any) => {
      const raw = String(value || '').trim();
      if (!raw) return '';
      if (sceneNameById.has(raw)) return raw;
      return sceneIdByName.get(raw.toLowerCase()) || raw;
    };

    const resolveCharacterId = (value: any) => {
      const raw = String(value || '').trim();
      if (!raw) return '';
      if (characterNameById.has(raw)) return raw;
      return characterIdByName.get(raw.toLowerCase()) || raw;
    };

    const existing = new Map<string, any>();
    const addConnection = (connection: any) => {
      const sourceId = String(connection.sourceId || connection.source_id || '').trim();
      const targetId = String(connection.targetId || connection.target_id || '').trim();
      const relationshipType = String(connection.relationshipType || connection.relationship_type || '').trim();
      if (!sourceId || !targetId || !relationshipType || sourceId === targetId) {
        return;
      }
      const key = `${sourceId}::${targetId}::${relationshipType}`;
      if (existing.has(key)) {
        return;
      }
      existing.set(key, {
        id: connection.id || `auto-${key}`,
        sourceId,
        targetId,
        relationshipType,
        semanticFamily:
          connection.semanticFamily ||
          connection.semantic_family ||
          inferRelationshipFamily(relationshipType, 'structural'),
        strength: Number(connection.strength) || 1,
      });
    };

    connections.forEach(addConnection);

    const sceneCharacters = new Map<string, string[]>();
    sceneElements.forEach((scene) => {
      const resolvedIds = Array.from(
        new Set(
          arr(scene.data?.characters_present)
            .map(resolveCharacterId)
            .filter((id) => id && characterNameById.has(id))
        )
      );
      sceneCharacters.set(String(scene.id), resolvedIds);
      resolvedIds.forEach((characterId) => {
        addConnection({
          sourceId: characterId,
          targetId: String(scene.id),
          relationshipType: 'participates_in',
          semanticFamily: 'participation',
        });
      });
      for (let i = 0; i < resolvedIds.length; i += 1) {
        for (let j = i + 1; j < resolvedIds.length; j += 1) {
          const pair = [resolvedIds[i], resolvedIds[j]].sort();
          addConnection({
            sourceId: pair[0],
            targetId: pair[1],
            relationshipType: 'shares_scene_with',
            semanticFamily: 'emotional',
          });
        }
      }
    });

    eventElements.forEach((event) => {
      const sceneId = resolveSceneId(event.data?.scene_id || event.data?.sceneId);
      if (sceneId && sceneNameById.has(sceneId)) {
        addConnection({
          sourceId: sceneId,
          targetId: String(event.id),
          relationshipType: 'contains',
          semanticFamily: 'structural',
        });
      }
      arr(event.data?.characters_involved)
        .map(resolveCharacterId)
        .filter((characterId) => characterId && characterNameById.has(characterId))
        .forEach((characterId) => {
          addConnection({
            sourceId: characterId,
            targetId: String(event.id),
            relationshipType: 'participates_in',
            semanticFamily: 'participation',
          });
        });
    });

    plotDeviceElements.forEach((device) => {
      const deviceId = String(device.id);
      arr(device.data?.links?.scenes)
        .map(resolveSceneId)
        .filter((sceneId) => sceneId && sceneNameById.has(sceneId))
        .forEach((sceneId) => {
          addConnection({
            sourceId: deviceId,
            targetId: sceneId,
            relationshipType: 'affects',
            semanticFamily: 'causal',
          });
        });
      arr(device.data?.setup_nodes)
        .map((node: any) => resolveSceneId(node?.scene_id || node?.sceneId))
        .filter((sceneId) => sceneId && sceneNameById.has(sceneId))
        .forEach((sceneId) => {
          addConnection({
            sourceId: deviceId,
            targetId: sceneId,
            relationshipType: 'setup_in',
            semanticFamily: 'structural',
          });
        });
      arr(device.data?.payoff_nodes)
        .map((node: any) => resolveSceneId(node?.scene_id || node?.sceneId))
        .filter((sceneId) => sceneId && sceneNameById.has(sceneId))
        .forEach((sceneId) => {
          addConnection({
            sourceId: deviceId,
            targetId: sceneId,
            relationshipType: 'payoff_in',
            semanticFamily: 'structural',
          });
        });
      arr(device.data?.links?.characters)
        .map(resolveCharacterId)
        .filter((characterId) => characterId && characterNameById.has(characterId))
        .forEach((characterId) => {
          addConnection({
            sourceId: deviceId,
            targetId: characterId,
            relationshipType: 'affects',
            semanticFamily: 'causal',
          });
        });
    });

    return Array.from(existing.values());
  }, [
    characterIdByName,
    characterNameById,
    connections,
    eventElements,
    plotDeviceElements,
    sceneElements,
    sceneIdByName,
    sceneNameById,
  ]);
  const connectionCountByElement = useMemo(() => {
    const counts = new Map<string, number>();
    displayConnections.forEach((item) => {
      const sourceId = String(item.sourceId || '');
      const targetId = String(item.targetId || '');
      if (sourceId) counts.set(sourceId, (counts.get(sourceId) || 0) + 1);
      if (targetId) counts.set(targetId, (counts.get(targetId) || 0) + 1);
    });
    return counts;
  }, [displayConnections]);

  const reportCanvasError = (action: string, error?: any) => {
    const message = error?.response?.data?.error || error?.message || text.unknownError;
    console.error(`${action} failed`, error);
    window.alert(`${action}: ${message}`);
  };
  const relationshipFamilyColors: Record<string, string> = {
    emotional: '#d9485f',
    influence: '#4169e1',
    causal: '#2f9e44',
    participation: '#0b7285',
    structural: '#8f5c2c',
    custom: '#495057',
  };
  const inferRelationshipFamily = (value: string, fallback = 'influence') => {
    const normalized = String(value || '').trim();
    return connectionFamilyMap[normalized] || fallback;
  };

  const buildSceneDeviceMap = (items: any[]) => {
    const map: Record<string, any[]> = {};
    items.forEach((device) => {
      const linked = device?.links && Array.isArray(device.links.scenes) ? device.links.scenes : [];
      const setupScenes = Array.isArray(device?.setup_nodes)
        ? device.setup_nodes.map((node: any) => node?.scene_id).filter(Boolean)
        : [];
      const payoffScenes = Array.isArray(device?.payoff_nodes)
        ? device.payoff_nodes.map((node: any) => node?.scene_id).filter(Boolean)
        : [];
      const sceneIds = Array.from(new Set([...(linked || []), ...setupScenes, ...payoffScenes]));
      sceneIds.forEach((sceneId: string) => {
        map[sceneId] = map[sceneId] || [];
        map[sceneId].push(device);
      });
    });
    return map;
  };

  const normalizeLocalDevice = (item: any) => ({
    ...(item?.data || {}),
    id: String(item?.data?.backendId || item?.data?.id || item?.id || ''),
    title: String(item?.data?.title || item?.data?.name || item?.name || ''),
    device_type: String(item?.data?.device_type || ''),
    links: item?.data?.links || { scenes: [] },
    setup_nodes: Array.isArray(item?.data?.setup_nodes) ? item.data.setup_nodes : [],
    payoff_nodes: Array.isArray(item?.data?.payoff_nodes) ? item.data.payoff_nodes : [],
  });

  const localDevices = useMemo(
    () => plotDeviceElements.map((item) => normalizeLocalDevice(item)).filter((item) => item.id),
    [plotDeviceElements]
  );

  useEffect(() => {
    setDevicesByScene(buildSceneDeviceMap(localDevices));
  }, [localDevices]);

  const applyDeviceTypeHints = (items: any[]) => {
    const nextHints: Record<string, string[]> = {};
    items.forEach((item) => {
      nextHints[item.type] = Array.isArray(item.expected_evidence) ? item.expected_evidence : [];
    });
    setDeviceTypeHints(nextHints);
    return nextHints;
  };

  const fetchDeviceTypes = async () => {
    const response = await axios.get(`${API_BASE}/plot_devices/types`);
    const items: any[] = response.data?.types || [];
    applyDeviceTypeHints(items);
    return items;
  };

  const refreshDevicesByScene = async () => {
    const typesResponse = await axios.get(`${API_BASE}/plot_devices/types`);
    const typeItems: any[] = typesResponse.data?.types || [];
    applyDeviceTypeHints(typeItems);
    setDevicesByScene(buildSceneDeviceMap(localDevices));
    return localDevices;
  };

  const findLocalDevice = (deviceId: string) => localDevices.find((item) => String(item.id) === String(deviceId));

  const patchLocalDevice = async (deviceId: string, patch: Record<string, any>, successMessage: string) => {
    const target = plotDeviceElements.find((item) => String(item.id) === String(deviceId));
    if (!target) {
      window.alert(text.deviceMissing);
      return;
    }

    if (onElementPatch) {
      await onElementPatch(target.id, patch, successMessage);
      return;
    }

    target.data = { ...target.data, ...patch };
  };

  const canvasBounds = useMemo(() => {
    const defaultWidth = isMobileLayout ? 980 : 1480;
    const defaultHeight = isMobileLayout ? 780 : 980;

    return dramaElements.reduce(
      (bounds, element) => {
        const x = Number(element?.position?.x) || 0;
        const y = Number(element?.position?.y) || 0;
        return {
          width: Math.max(bounds.width, x + 340),
          height: Math.max(bounds.height, y + 280),
        };
      },
      { width: defaultWidth, height: defaultHeight }
    );
  }, [dramaElements, isMobileLayout]);

  const summarize = (value: any, limit = 42) => {
    const textValue = String(value || '').trim();
    if (!textValue) return '';
    return textValue.length > limit ? `${textValue.slice(0, limit)}…` : textValue;
  };

  const detailRowsForElement = (element: any) => {
    const rows: string[] = [];
    const type = element.type;
    if (type === 'character') {
      if (element.data?.character_type) rows.push(`${text.typeLabel}: ${element.data.character_type}`);
      if (element.data?.background) rows.push(`${locale === 'zh' ? '背景' : 'Background'}: ${summarize(element.data.background, 56)}`);
      if (element.data?.goal) rows.push(`${locale === 'zh' ? '目标' : 'Goal'}: ${summarize(element.data.goal)}`);
      if (element.data?.motivation) rows.push(`${locale === 'zh' ? '动机' : 'Motivation'}: ${summarize(element.data.motivation)}`);
      if (element.data?.obstacle) rows.push(`${locale === 'zh' ? '阻碍' : 'Obstacle'}: ${summarize(element.data.obstacle)}`);
      if (element.data?.arc) rows.push(`${locale === 'zh' ? '弧线' : 'Arc'}: ${summarize(element.data.arc)}`);
    } else if (type === 'scene') {
      const actLabel = enumDisplayLabel(String(element.data?.act || 'act_one'), locale, 'act');
      const episode = element.data?.episode ? String(element.data.episode) : '';
      if (actLabel || episode) rows.push([actLabel, episode].filter(Boolean).join(' · '));
      rows.push(
        `${locale === 'zh' ? '人物' : 'Cast'} ${Array.isArray(element.data?.characters_present) ? element.data.characters_present.length : 0} · ${locale === 'zh' ? '事件' : 'Events'} ${eventElements.filter((item) => String(item.data?.sceneId || item.data?.scene_id || '') === String(element.id)).length}`
      );
      if (element.data?.setting || element.data?.time) {
        rows.push(
          [element.data?.setting, element.data?.time]
            .map((value) => summarize(value, 28))
            .filter(Boolean)
            .join(' · ')
        );
      }
      if (element.data?.purpose) rows.push(`${locale === 'zh' ? '目的' : 'Purpose'}: ${summarize(element.data.purpose)}`);
      if (element.data?.conflict) rows.push(`${locale === 'zh' ? '冲突' : 'Conflict'}: ${summarize(element.data.conflict)}`);
      if (element.data?.mood) rows.push(`${locale === 'zh' ? '氛围' : 'Mood'}: ${summarize(element.data.mood, 24)}`);
    } else if (type === 'event') {
      if (element.data?.beat) rows.push(`${locale === 'zh' ? '节拍' : 'Beat'}: ${enumDisplayLabel(String(element.data.beat), locale, 'beat')}`);
      const sceneId = String(element.data?.sceneId || element.data?.scene_id || '');
      if (sceneId) rows.push(`${locale === 'zh' ? '场景' : 'Scene'}: ${sceneNameById.get(sceneId) || sceneId}`);
      if (Array.isArray(element.data?.characters_involved) && element.data.characters_involved.length) {
        rows.push(
          `${locale === 'zh' ? '涉及人物' : 'Cast'}: ${element.data.characters_involved
            .slice(0, 3)
            .map((id: string) => characterNameById.get(String(id)) || id)
            .join(' / ')}`
        );
      }
      if (element.data?.conflict_type) rows.push(`${locale === 'zh' ? '冲突类型' : 'Conflict type'}: ${element.data.conflict_type}`);
      if (element.data?.description) rows.push(summarize(element.data.description));
    } else if (type === 'plot_device' || type === 'device') {
      if (element.data?.device_type) rows.push(`${text.typeLabel}: ${element.data.device_type}`);
      if (element.data?.short_drama_function) {
        rows.push(`${locale === 'zh' ? '短剧作用' : 'Drama use'}: ${element.data.short_drama_function}`);
      }
      if (Array.isArray(element.data?.short_drama_tags) && element.data.short_drama_tags.length) {
        rows.push(`${locale === 'zh' ? '标签' : 'Tags'}: ${element.data.short_drama_tags.slice(0, 3).join(' / ')}`);
      }
      if (Array.isArray(element.data?.recommended_catharsis_types) && element.data.recommended_catharsis_types.length) {
        rows.push(`${locale === 'zh' ? '爽点型' : 'Catharsis'}: ${element.data.recommended_catharsis_types.slice(0, 2).join(' / ')}`);
      }
      const sceneLinks = element.data?.links?.scenes || [];
      rows.push(`${locale === 'zh' ? '绑定场景' : 'Scene links'}: ${sceneLinks.length}`);
      rows.push(
        `${locale === 'zh' ? 'Setup / Payoff' : 'Setup / payoff'}: ${(element.data?.setup_nodes || []).length} / ${(element.data?.payoff_nodes || []).length}`
      );
      if (Array.isArray(element.data?.expected_evidence) && element.data.expected_evidence.length) {
        rows.push(`${locale === 'zh' ? '证据' : 'Evidence'}: ${element.data.expected_evidence.slice(0, 2).join(' / ')}`);
      }
      if (element.data?.description) rows.push(summarize(element.data.description));
    } else if (type === 'dialogue') {
      if (element.data?.speaker) rows.push(`${locale === 'zh' ? '说话人' : 'Speaker'}: ${element.data.speaker}`);
      if (element.data?.emotion) rows.push(`${locale === 'zh' ? '情绪' : 'Emotion'}: ${element.data.emotion}`);
      if (element.data?.subtext) rows.push(`${locale === 'zh' ? '潜台词' : 'Subtext'}: ${summarize(element.data.subtext)}`);
      if (element.data?.content) rows.push(summarize(element.data.content));
    } else if (type === 'theme') {
      if (element.data?.importance) rows.push(`${locale === 'zh' ? '重要度' : 'Importance'}: ${element.data.importance}`);
      if (element.data?.description) rows.push(summarize(element.data.description));
    } else if (type === 'world_state') {
      if (element.data?.genre) rows.push(`${text.typeLabel}: ${element.data.genre}`);
      if (element.data?.location) rows.push(`${locale === 'zh' ? '地点' : 'Location'}: ${summarize(element.data.location)}`);
      if (Array.isArray(element.data?.rules) && element.data.rules.length) rows.push(`${locale === 'zh' ? '规则' : 'Rules'}: ${element.data.rules.length}`);
      if (element.data?.social_hierarchy) rows.push(`${locale === 'zh' ? '秩序' : 'Order'}: ${summarize(element.data.social_hierarchy, 48)}`);
    } else if (type === 'arc_milestone') {
      if (element.data?.milestone_type) rows.push(`${locale === 'zh' ? '里程碑' : 'Milestone'}: ${element.data.milestone_type}`);
      if (element.data?.character_id) rows.push(`${locale === 'zh' ? '角色' : 'Character'}: ${characterNameById.get(String(element.data.character_id)) || element.data.character_id}`);
      if (element.data?.attached_beat) rows.push(`${locale === 'zh' ? '绑定节拍' : 'Beat'}: ${element.data.attached_beat}`);
      if (element.data?.note) rows.push(summarize(element.data.note));
    }
    return rows.filter(Boolean).slice(0, 6);
  };

  // 获取装置类型 Schema
  const fetchDeviceTypeSchema = async (deviceType: string) => {
    if (deviceTypeSchemas[deviceType]) {
      return deviceTypeSchemas[deviceType];
    }
    try {
      const response = await axios.get(`${API_BASE}/plot_devices/type_schema/${deviceType}`);
      const schema = response.data?.schema;
      if (schema) {
        setDeviceTypeSchemas(prev => ({ ...prev, [deviceType]: schema }));
        return schema;
      }
    } catch (error) {
      console.error('Failed to fetch device type schema:', error);
    }
    return null;
  };

  // 渲染差异化字段表单
  const renderDeviceTypeFields = (elementId: string, deviceType: string) => {
    const schema = deviceTypeSchemas[deviceType];
    if (!schema || !schema.fields) return null;

    const fieldsContainer = document.getElementById(`device-fields-${elementId}`);
    if (!fieldsContainer) return;

    // 清空现有内容
    fieldsContainer.innerHTML = '';
    fieldsContainer.style.display = 'block';

    // 渲染字段
    schema.fields.forEach((field: any) => {
      if (field.name === 'title' || field.name === 'links') return; // 跳过已处理的字段

      const fieldDiv = document.createElement('div');
      fieldDiv.style.marginBottom = '6px';
      fieldDiv.style.display = 'flex';
      fieldDiv.style.alignItems = 'center';
      fieldDiv.style.gap = '6px';

      const label = document.createElement('span');
      label.textContent = field.name;
      label.style.fontSize = '11px';
      label.style.minWidth = '80px';
      label.style.color = '#666';

      let input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

      if (field.type === 'array') {
        input = document.createElement('textarea');
        input.placeholder = text.arrayOrJsonInput;
        input.rows = 2;
        if (field.suggest && Array.isArray(field.suggest)) {
          input.placeholder += `\n${text.evidence}: ${field.suggest.join(', ')}`;
        }
      } else if (field.type === 'number') {
        input = document.createElement('input');
        input.type = 'number';
        input.step = '0.1';
        input.min = '0';
        input.max = '1';
        if (field.default !== undefined) {
          input.value = field.default.toString();
        }
      } else {
        input = document.createElement('input');
        input.type = 'text';
        if (field.suggest && Array.isArray(field.suggest)) {
          input.placeholder = `${text.evidence}: ${field.suggest.join(', ')}`;
        }
      }

      input.id = `new-device-${field.name}-${elementId}`;
      input.style.fontSize = '11px';
      input.style.padding = '2px 6px';
      input.style.border = '1px solid #ddd';
      input.style.borderRadius = '3px';
      input.style.flex = '1';

      fieldDiv.appendChild(label);
      fieldDiv.appendChild(input);
      fieldsContainer.appendChild(fieldDiv);
    });
  };

  // 自定义形状渲染
  const renderDramaElement = (element: any) => {
    const icons: Record<string, string> = {
      character: '👤',
      event: '⚡',
      scene: '🎭',
      theme: '💭',
      world: '🌍',
      world_state: '🌍',
      dialogue: '💬',
      device: '🔧'
    };

    const typeLabels: Record<string, string> = {
      character: text.character,
      event: text.event,
      scene: text.scene,
      theme: text.theme,
      world: text.worldview,
      world_state: text.worldview,
      dialogue: text.dialogue,
      device: text.device
    };

    const MAX_NAME_LENGTH = 10; // 最大显示字符数
    icons.plot_device = '🔧';
    icons.arc_milestone = '🧭';
    typeLabels.plot_device = text.plotDevice;
    typeLabels.arc_milestone = text.arcMilestone;
    const isExpanded = expandedElements[element.id] || false;
    const displayName = element.name || text.unnamed;
    const shouldTruncate = displayName.length > MAX_NAME_LENGTH;
    const truncatedName = shouldTruncate && !isExpanded 
      ? displayName.substring(0, MAX_NAME_LENGTH) + '...' 
      : displayName;

    const isDragging = draggingElement === element.id;
    const isHovered = hoveredElement === element.id;
    const isSelected = selectedId === element.id;
    const zIndex = isDragging ? 2000 : (isExpanded ? 1000 : (isHovered ? 100 : 1));
    const detailRows = detailRowsForElement(element);
    const relationCount = connectionCountByElement.get(String(element.id)) || 0;
    const typeLabel = typeLabels[element.type] || element.type;

    const handleMouseDown = (e: React.MouseEvent) => {
      const target = e.target;
      if (
        e.button !== 0 ||
        (target instanceof HTMLElement &&
          target.closest('button, input, select, textarea, a, .expand-btn, .delete-btn, .edit-btn'))
      ) {
        return;
      }

      e.preventDefault();
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setPendingDrag({
        elementId: element.id,
        startX: e.clientX,
        startY: e.clientY,
        offsetX: (e.clientX - rect.left) / zoom,
        offsetY: (e.clientY - rect.top) / zoom,
      });
    };

    return (
      <div
        key={element.id}
        style={{
          position: 'absolute',
          left: element.position.x,
          top: element.position.y,
          width: isExpanded ? '300px' : '248px',
          minWidth: '220px',
          maxWidth: isExpanded ? '320px' : '260px',
          minHeight: '116px',
          background: '#ffffff',
          border: isSelected ? '2px solid #0f8a6a' : '2px solid #1a1a1a',
          borderRadius: '16px',
          padding: '12px',
          cursor: isDragging ? 'grabbing' : 'grab',
          boxShadow: isDragging
            ? '0 8px 24px rgba(0,0,0,0.2)'
            : isSelected
              ? '0 10px 28px rgba(15,138,106,0.18)'
              : '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          fontFamily: "'Noto Serif SC', '思源宋体', serif",
          transition: isDragging ? 'none' : 'all 0.3s ease',
          zIndex: zIndex,
          opacity: isDragging ? 0.9 : 1,
          pointerEvents: 'auto'
        }}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setHoveredElement(element.id)}
        onMouseLeave={() => {
          if (!isDragging && pendingDrag?.elementId !== element.id) {
            setHoveredElement(null);
          }
        }}
        onClick={(e) => {
          // 如果点击的是展开/收起按钮或删除按钮，不触发元素选择
          const target = e.target;
          if (
            target instanceof HTMLElement &&
            target.closest('button, input, select, textarea, a, .expand-btn, .delete-btn, .edit-btn')
          ) {
            return;
          }
          // 如果正在拖拽，不触发点击
          if (draggingElement === element.id) {
            return;
          }
          handleElementClick(element.id);
        }}
        onDoubleClick={() => {
          if (!isDragging && onElementEdit) {
            onElementEdit(element);
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
          <div style={{ display: 'flex', gap: '10px', minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '22px', lineHeight: 1, marginTop: '2px' }}>
              {icons[element.type as keyof typeof icons] || '🔶'}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  fontFamily: "'Noto Serif SC', '思源宋体', serif",
                  wordBreak: 'break-word',
                  lineHeight: '1.35',
                }}
                title={displayName}
              >
                {truncatedName}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                <span
                  style={{
                    fontSize: '10px',
                    color: '#3d3427',
                    background: '#f4ede0',
                    border: '1px solid #d4c7af',
                    borderRadius: '999px',
                    padding: '2px 8px',
                  }}
                >
                  {`${text.typeBadge}: ${typeLabel}`}
                </span>
                <span
                  style={{
                    fontSize: '10px',
                    color: '#0f5d49',
                    background: '#eef8f4',
                    border: '1px solid #9bd3be',
                    borderRadius: '999px',
                    padding: '2px 8px',
                  }}
                >
                  {`${text.relationCount} ${relationCount}`}
                </span>
              </div>
            </div>
          </div>
          {shouldTruncate && (
            <button
              className="expand-btn"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedElements((prev) => ({ ...prev, [element.id]: !prev[element.id] }));
              }}
              style={{
                fontSize: '10px',
                padding: '3px 8px',
                background: '#fef9e7',
                border: '1px solid #1a1a1a',
                borderRadius: '999px',
                cursor: 'pointer',
                color: '#1a1a1a',
                fontFamily: "'Noto Serif SC', '思源宋体', serif",
                whiteSpace: 'nowrap',
              }}
            >
              {isExpanded ? text.collapse : text.expand}
            </button>
          )}
        </div>
        {detailRows.length > 0 && (
          <div
            style={{
              display: 'grid',
              gap: '4px',
              marginBottom: '10px',
              padding: '10px',
              background: '#faf7f1',
              border: '1px solid #e5dccd',
              borderRadius: '12px',
            }}
          >
            {detailRows.map((row, index) => (
              <div
                key={`${element.id}-detail-${index}`}
                style={{
                  fontSize: '10px',
                  color: '#4b4134',
                  lineHeight: 1.45,
                  fontFamily: "'Noto Serif SC', '思源宋体', serif",
                  wordBreak: 'break-word',
                }}
              >
                {row}
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <button
            className="edit-btn"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleElementEdit(element);
            }}
            style={{
              fontSize: '10px',
              padding: '4px 10px',
              background: '#fff',
              border: '1px solid #1a1a1a',
              borderRadius: '999px',
              cursor: 'pointer',
              color: '#1a1a1a',
              fontFamily: "'Noto Serif SC', '思源宋体', serif",
            }}
          >
            {text.editDetails}
          </button>
        </div>
        <div style={{ fontSize: '24px', marginBottom: '4px', display: 'none' }}>
          {icons[element.type as keyof typeof icons] || '📌'}
        </div>
        <div style={{ 
          fontSize: '12px', 
          textAlign: 'center', 
          fontWeight: 700,
          color: '#1a1a1a',
          fontFamily: "'Noto Serif SC', '思源宋体', serif",
          wordBreak: isExpanded ? 'break-word' : 'normal',
          lineHeight: '1.3',
          marginBottom: '4px',
          display: 'none'
        }}>
          {truncatedName}
        </div>
        {shouldTruncate && (
          <button
            className="expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedElements(prev => ({ ...prev, [element.id]: !prev[element.id] }));
            }}
            style={{
              fontSize: '9px',
              display: 'none',
              padding: '2px 6px',
              marginTop: '2px',
              background: '#fef9e7',
              border: '1px solid #1a1a1a',
              borderRadius: '4px',
              cursor: 'pointer',
              color: '#1a1a1a',
              fontFamily: "'Noto Serif SC', '思源宋体', serif"
            }}
          >
            {isExpanded ? text.collapse : text.expand}
          </button>
        )}
        <div style={{ 
          fontSize: '10px', 
          color: '#666', 
          textAlign: 'center',
          fontFamily: "'Noto Serif SC', '思源宋体', serif",
          marginTop: '4px',
          display: 'none'
        }}>
          {typeLabels[element.type] || element.type}
        </div>
        {/* 事件图片显示 */}
        {element.type === 'event' && (
          <div style={{ marginTop: '8px', width: '100%', position: 'relative' }}>
            {eventImages[element.id] ? (
              <img
                src={eventImages[element.id]}
                alt={element.name}
                style={{
                  width: '100%',
                  maxHeight: '120px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(eventImages[element.id], '_blank');
                }}
                onError={(e) => {
                  // 图片加载失败时隐藏
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  const description = element.data?.description || element.name || '';
                  if (!description) {
                    alert(text.emptyEventDescription);
                    return;
                  }
                  
                  setGeneratingImages(prev => ({ ...prev, [element.id]: true }));
                  try {
                    const response = await axios.post(`${API_BASE}/api/generate-event-image`, {
                      description: description,
                      event_id: element.id
                    });
                    
                    if (response.data.success && response.data.image_url) {
                      setEventImages(prev => ({ ...prev, [element.id]: response.data.image_url }));
                    } else {
                      alert(`${text.imageGenerationFailed}: ${response.data.error || text.unknownError}`);
                    }
                  } catch (error: any) {
                    console.error(text.imageGenerationFailed, error);
                    alert(`${text.imageGenerationFailed}: ${error.response?.data?.error || error.message}`);
                  } finally {
                    setGeneratingImages(prev => ({ ...prev, [element.id]: false }));
                  }
                }}
                disabled={generatingImages[element.id]}
                style={{
                  width: '100%',
                  padding: '6px',
                  fontSize: '10px',
                  background: generatingImages[element.id] ? '#ccc' : '#fef9e7',
                  border: '1px solid #1a1a1a',
                  borderRadius: '6px',
                  cursor: generatingImages[element.id] ? 'not-allowed' : 'pointer',
                  color: '#1a1a1a',
                  fontFamily: "'Noto Serif SC', '思源宋体', serif",
                  pointerEvents: 'auto'
                }}
              >
                {generatingImages[element.id] ? text.generatingImage : text.generateImage}
              </button>
            )}
          </div>
        )}
        {/* 删除按钮 */}
        {onElementDelete && (
          <button
            className="delete-btn"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onElementDelete(element.id);
            }}
            style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              width: '24px',
              height: '24px',
              fontSize: '16px',
              padding: 0,
              background: '#ff4444',
              color: '#ffffff',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              opacity: isHovered || isSelected || isDragging ? 1 : 0.82,
              transition: 'opacity 0.2s ease',
              zIndex: 10,
              pointerEvents: 'auto'
            }}
            title={text.delete}
          >
            ×
          </button>
        )}
        {/* 情境/事件的结构菜单 */}
        {element.type === 'scene' && (
          <div style={{ marginTop: 6 }}>
            <select
              value={element.data?.act || 'act_one'}
              onChange={(e) => onSetAct(element.id, e.target.value)}
              style={{ fontSize: 10 }}
            >
              {actOrder.map((value) => (
                <option key={value} value={value}>
                  {enumDisplayLabel(value, locale, 'act')}
                </option>
              ))}
            </select>
            {/* 场景内节拍时间线：根据绑定到该场景的事件可视化 */}
            <div style={{ marginTop: 8 }}>
              <div style={{ height: 4, background: '#e9ecef', borderRadius: 2, position: 'relative' }} />
              <div style={{ position: 'relative', height: 20, marginTop: 6 }}>
                {eventElements
                  .filter((ev) => {
                    const sceneId = String(ev.data?.sceneId || ev.data?.scene_id || '');
                    const currentSceneId = String(element.id || element.data?.backendId || '');
                    return sceneId === currentSceneId && ev.data?.beat;
                  })
                  .sort((a, b) => beatOrder.indexOf(a.data.beat) - beatOrder.indexOf(b.data.beat))
                  .map((ev, idx, arr) => {
                    const pos = beatOrder.indexOf(ev.data.beat);
                    const leftPercent = (pos / (beatOrder.length - 1)) * 100;
                    return (
                      <div key={ev.id} title={`${ev.name} · ${enumDisplayLabel(String(ev.data.beat || ''), locale, 'beat')}`}
                        style={{ position: 'absolute', left: `calc(${leftPercent}% - 4px)`, top: 2, width: 8, height: 8, borderRadius: 4, background: '#667eea', boxShadow: '0 0 0 2px #fff' }} />
                    );
                  })}
              </div>
            </div>
            {/* 冲突：徽标与编辑 */}
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                {(element.data?.conflict_axes || []).slice(0, 3).map((ax: string, i: number) => (
                  <span key={i} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, background: '#fff3cd', border: '1px solid #ffe69c', color: '#7a5e00' }}>{ax}</span>
                ))}
                {(element.data?.conflict_axes || []).length > 3 && (
                  <span style={{ fontSize: 10, color: '#999' }}>+{(element.data?.conflict_axes.length - 3)}</span>
                )}
              </div>
              {typeof element.data?.conflict_intensity === 'number' && element.data.conflict_intensity > 0 && (
                <div title={`${text.intensity} ${element.data.conflict_intensity}`} style={{ marginTop: 4, width: '80%', height: 6, background: '#eee', borderRadius: 3 }}>
                  <div style={{ width: `${Math.min(100, element.data.conflict_intensity * 20)}%`, height: '100%', background: '#ff6b6b', borderRadius: 3 }} />
                </div>
              )}
              <button
                style={{ marginTop: 6, fontSize: 10, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
                onClick={async () => {
                  const axesStr = prompt(text.conflictAxesPrompt, (element.data?.conflict_axes || []).join(',')) || '';
                  const intensityStr = prompt(text.conflictIntensityPrompt, String(element.data?.conflict_intensity || 3)) || '3';
                  const rolesStr = prompt(text.conflictRolesPrompt, (element.data?.conflict_roles || []).join(',')) || '';
                  const pairsHint = text.conflictPairsHint;
                  const pairsStr = prompt(`${text.conflictPairsPrompt}\n${pairsHint}`, '') || '';
                  const axes = axesStr.split(',').map(s => s.trim()).filter(Boolean);
                  const intensity = Math.max(1, Math.min(5, parseInt(intensityStr, 10) || 3));
                  const conflict_roles = rolesStr.split(',').map(s => s.trim()).filter(Boolean);
                  const conflict_pairs = pairsStr.split(';').map(p => p.trim()).filter(Boolean).map(p => {
                    const [source_id, target_id, inten, ...noteParts] = p.split(':');
                    return { source_id, target_id, intensity: Math.max(1, Math.min(5, parseInt(inten || '3', 10))), note: noteParts.join(':') };
                  });
                  try {
                    if (onElementPatch) {
                      await onElementPatch(
                        element.id,
                        { conflict_axes: axes, conflict_intensity: intensity, conflict_roles, conflict_pairs },
                        text.conflictDrafted
                      );
                    } else {
                      element.data = { ...element.data, conflict_axes: axes, conflict_intensity: intensity, conflict_roles, conflict_pairs };
                    }
                  } catch (error) {
                    reportCanvasError(text.conflictUpdateAction, error);
                  }
                }}
              >{text.editConflict}</button>
            </div>

            {/* 戏剧装置：徽标、刷新与编辑 */}
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                {(devicesByScene[element.id] || []).slice(0, 3).map((dv: any, i: number) => (
                  <span key={i} title={(dv.title ? (dv.title + "\n") : '') + (dv.device_type ? (`${text.typeLabel}: ${dv.device_type}\n${text.evidence}: ${(deviceTypeHints[dv.device_type] || []).join(' / ')}`) : '')}
                    style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, background: '#e7f5ff', border: '1px solid #a5d8ff', color: '#0b7285' }}>
                    {dv.device_type || 'device'}
                  </span>
                ))}
                {(devicesByScene[element.id] || []).length > 3 && (
                  <span style={{ fontSize: 10, color: '#999' }}>+{(devicesByScene[element.id].length - 3)}</span>
                )}
              </div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
                  onClick={async () => {
                    try {
                      await refreshDevicesByScene();
                    } catch (error) {
                      reportCanvasError(text.refreshDevicesAction, error);
                    }
                  }}
                >{text.refreshDevices}</button>
                <button
                  style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
                  onClick={async () => {
                    const title = prompt(text.deviceTitlePrompt, '') || '';
                    // 从后端获取类型清单
                    let device_type = '';
                    try {
                      const r = await axios.get(`${API_BASE}/plot_devices/types`);
                      const items: any[] = r.data?.types || [];
                      const names = items.map((it: any) => it.type).join(', ');
                      device_type = prompt(`${text.deviceTypePrompt}\n${names}`, items[0]?.type || '') || '';
                    } catch {
                      device_type = prompt(text.deviceTypeFallbackPrompt, '') || '';
                    }
                    const backendId = element.data?.backendId || element.id;
                    try {
                      onElementCreateFromDraft?.('plot_device', {
                        title,
                        name: title,
                        device_type,
                        links: { scenes: [backendId] },
                        setup_nodes: [],
                        payoff_nodes: [],
                        coverage: {},
                      });
                    } catch (error) {
                      reportCanvasError(text.addDeviceAction, error);
                    }
                  }}
                >{text.addDevice}</button>
                <button
                  style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
                  onClick={async () => {
                    // 打开装置编辑面板（下拉+可搜索）
                    const show = { ...showDeviceEditorByScene };
                    show[element.id] = !show[element.id];
                    setShowDeviceEditorByScene(show);
                    if (Object.keys(deviceTypeHints).length === 0) {
                      try {
                        const tr = await axios.get(`${API_BASE}/plot_devices/types`);
                        const titems: any[] = tr.data?.types || [];
                        const hintMap: Record<string, string[]> = {};
                        titems.forEach((it: any) => { hintMap[it.type] = Array.isArray(it.expected_evidence) ? it.expected_evidence : []; });
                        setDeviceTypeHints(hintMap);
                      } catch (error) {
                        reportCanvasError(text.loadDeviceTypesAction, error);
                      }
                    }
                  }}
                >{text.deviceEditor}</button>
                <button
                  style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
                  onClick={async () => {
                    const list = devicesByScene[element.id] || [];
                    if (!list.length) { alert(text.refreshOrCreateDevice); return; }
                    const typeFilter = prompt(text.deviceTypeFilterPrompt, '') || '';
                    const filtered = typeFilter ? list.filter(d => (d.device_type||'').toLowerCase() === typeFilter.toLowerCase()) : list;
                    if (!filtered.length) { alert(text.noMatchingDevices); return; }
                    const id = prompt(text.setupSelectionPrompt, filtered[0]?.id || '');
                    if (!id) return;
                    const backendId = element.data?.backendId || element.id;
                    try {
                      const device = findLocalDevice(id);
                      const currentSetupNodes = Array.isArray(device?.setup_nodes) ? device?.setup_nodes : [];
                      const nextSetupNodes = [
                        ...currentSetupNodes.filter((node: any) => String(node?.scene_id) !== String(backendId)),
                        { scene_id: backendId, hint: 'scene-setup' },
                      ];
                      await patchLocalDevice(id, { setup_nodes: nextSetupNodes }, text.setupDrafted);
                    } catch (error) {
                      reportCanvasError(text.markSetupAction, error);
                    }
                  }}
                >{text.markCurrentSceneSetup}</button>
                <button
                  style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
                  onClick={async () => {
                    const list = devicesByScene[element.id] || [];
                    if (!list.length) { alert(text.refreshOrCreateDevice); return; }
                    const typeFilter = prompt(text.deviceTypeFilterPrompt, '') || '';
                    const filtered = typeFilter ? list.filter(d => (d.device_type||'').toLowerCase() === typeFilter.toLowerCase()) : list;
                    if (!filtered.length) { alert(text.noMatchingDevices); return; }
                    const id = prompt(text.payoffSelectionPrompt, filtered[0]?.id || '');
                    if (!id) return;
                    const backendId = element.data?.backendId || element.id;
                    try {
                      const device = findLocalDevice(id);
                      const currentPayoffNodes = Array.isArray(device?.payoff_nodes) ? device?.payoff_nodes : [];
                      const nextPayoffNodes = [
                        ...currentPayoffNodes.filter((node: any) => String(node?.scene_id) !== String(backendId)),
                        { scene_id: backendId, hint: 'scene-payoff' },
                      ];
                      await patchLocalDevice(id, { payoff_nodes: nextPayoffNodes }, text.payoffDrafted);
                    } catch (error) {
                      reportCanvasError(text.markPayoffAction, error);
                    }
                  }}
                >{text.markCurrentScenePayoff}</button>
              </div>
            </div>
            {/* 装置编辑面板 */}
            {showDeviceEditorByScene[element.id] && (
              <div style={{ marginTop: 8, border: '1px solid #eee', borderRadius: 6, padding: 8, width: '100%', background: '#fafafa' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12 }}>{text.typeLabel}</span>
                    <select
                      value={selectedDeviceTypeByScene[element.id] || ''}
                      onChange={(e)=>{
                        const v = e.target.value;
                        setSelectedDeviceTypeByScene({ ...selectedDeviceTypeByScene, [element.id]: v });
                        setSelectedDeviceIdByScene({ ...selectedDeviceIdByScene, [element.id]: '' });
                      }}
                      style={{ fontSize: 12 }}
                    >
                      <option value=''>{text.all}</option>
                      {Object.keys(deviceTypeHints).map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 220 }}>
                    <span style={{ fontSize: 12 }}>{text.deviceLabel}</span>
                    <select
                      value={selectedDeviceIdByScene[element.id] || ''}
                      onChange={(e)=> setSelectedDeviceIdByScene({ ...selectedDeviceIdByScene, [element.id]: e.target.value })}
                      style={{ fontSize: 12, minWidth: 160 }}
                    >
                      {(devicesByScene[element.id] || []).filter((d: any) => {
                        const tp = selectedDeviceTypeByScene[element.id];
                        return !tp || (d.device_type || '').toLowerCase() === tp.toLowerCase();
                      }).map((d: any) => (
                        <option key={d.id} value={d.id}>{d.title || d.id} ({d.device_type || text.device.toLowerCase()})</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ fontSize: 12, color: '#666', maxWidth: 260 }}>
                    {(selectedDeviceTypeByScene[element.id] && deviceTypeHints[selectedDeviceTypeByScene[element.id]]) ? `${text.evidence}: ${deviceTypeHints[selectedDeviceTypeByScene[element.id]].join(' / ')}` : ''}
                  </div>
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    style={{ fontSize: 12, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
                    onClick={async () => {
                      const did = selectedDeviceIdByScene[element.id];
                      if (!did) { alert(text.chooseDevice); return; }
                      const backendId = element.data?.backendId || element.id;
                      try {
                        const device = findLocalDevice(did);
                        const currentSetupNodes = Array.isArray(device?.setup_nodes) ? device?.setup_nodes : [];
                        const nextSetupNodes = [
                          ...currentSetupNodes.filter((node: any) => String(node?.scene_id) !== String(backendId)),
                          { scene_id: backendId, hint: 'scene-setup' },
                        ];
                        await patchLocalDevice(did, { setup_nodes: nextSetupNodes }, text.setupDrafted);
                      } catch (error) {
                        reportCanvasError(text.markSetupAction, error);
                      }
                    }}
                  >{text.markSetupAction}</button>
                  <button
                    style={{ fontSize: 12, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
                    onClick={async () => {
                      const did = selectedDeviceIdByScene[element.id];
                      if (!did) { alert(text.chooseDevice); return; }
                      const backendId = element.data?.backendId || element.id;
                      try {
                        const device = findLocalDevice(did);
                        const currentPayoffNodes = Array.isArray(device?.payoff_nodes) ? device?.payoff_nodes : [];
                        const nextPayoffNodes = [
                          ...currentPayoffNodes.filter((node: any) => String(node?.scene_id) !== String(backendId)),
                          { scene_id: backendId, hint: 'scene-payoff' },
                        ];
                        await patchLocalDevice(did, { payoff_nodes: nextPayoffNodes }, text.payoffDrafted);
                      } catch (error) {
                        reportCanvasError(text.markPayoffAction, error);
                      }
                    }}
                  >{text.markPayoffAction}</button>
                  <button
                    style={{ fontSize: 12, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
                    onClick={async () => {
                      const did = selectedDeviceIdByScene[element.id];
                      if (!did) { alert(text.chooseDevice); return; }
                      
                      // 获取选中的装置信息
                      const selectedDevice = (devicesByScene[element.id] || []).find(d => d.id === did);
                      if (!selectedDevice) { alert(text.deviceMissing); return; }
                      
                      // 获取装置类型的 Schema
                      const schema = await fetchDeviceTypeSchema(selectedDevice.device_type);
                      if (!schema) { alert(text.unavailableDeviceTypeInfo); return; }
                      
                      // 显示编辑对话框
                      const editDialog = document.createElement('div');
                      editDialog.style.position = 'fixed';
                      editDialog.style.top = '50%';
                      editDialog.style.left = '50%';
                      editDialog.style.transform = 'translate(-50%, -50%)';
                      editDialog.style.background = 'white';
                      editDialog.style.border = '2px solid #333';
                      editDialog.style.borderRadius = '8px';
                      editDialog.style.padding = '20px';
                      editDialog.style.zIndex = '1000';
                      editDialog.style.minWidth = '400px';
                      editDialog.style.maxHeight = '80vh';
                      editDialog.style.overflow = 'auto';
                      
                      editDialog.innerHTML = `
                        <h3 style="margin: 0 0 15px 0; font-size: 16px;">${text.editDeviceTitle}: ${selectedDevice.title}</h3>
                        <div id="edit-device-fields"></div>
                        <div style="margin-top: 15px; display: flex; gap: 10px; justify-content: flex-end;">
                          <button id="cancel-edit" style="padding: 5px 15px; border: 1px solid #ccc; background: #f5f5f5; cursor: pointer;">${text.cancel}</button>
                          <button id="save-edit" style="padding: 5px 15px; border: 1px solid #007bff; background: #007bff; color: white; cursor: pointer;">${text.save}</button>
                        </div>
                      `;
                      
                      document.body.appendChild(editDialog);
                      
                      // 渲染编辑字段
                      const fieldsContainer = document.getElementById('edit-device-fields');
                      if (fieldsContainer && schema.fields) {
                        schema.fields.forEach((field: any) => {
                          if (field.name === 'title' || field.name === 'links') return;
                          
                          const fieldDiv = document.createElement('div');
                          fieldDiv.style.marginBottom = '10px';
                          
                          const label = document.createElement('label');
                          label.textContent = field.name;
                          label.style.display = 'block';
                          label.style.marginBottom = '5px';
                          label.style.fontWeight = 'bold';
                          label.style.fontSize = '12px';
                          
                          let input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
                          const currentValue = selectedDevice[field.name];
                          
                          if (field.type === 'array') {
                            input = document.createElement('textarea');
                            input.rows = 3;
                            input.value = Array.isArray(currentValue) ? currentValue.join(', ') : (currentValue || '');
                            input.placeholder = text.arrayInput;
                          } else if (field.type === 'number') {
                            input = document.createElement('input');
                            input.type = 'number';
                            input.step = '0.1';
                            input.min = '0';
                            input.max = '1';
                            input.value = currentValue || field.default || '';
                          } else {
                            input = document.createElement('input');
                            input.type = 'text';
                            input.value = currentValue || '';
                          }
                          
                          input.id = `edit-${field.name}`;
                          input.style.width = '100%';
                          input.style.padding = '5px';
                          input.style.border = '1px solid #ddd';
                          input.style.borderRadius = '3px';
                          input.style.fontSize = '12px';
                          
                          fieldDiv.appendChild(label);
                          fieldDiv.appendChild(input);
                          fieldsContainer.appendChild(fieldDiv);
                        });
                      }
                      
                      // 事件处理
                      document.getElementById('cancel-edit')?.addEventListener('click', () => {
                        document.body.removeChild(editDialog);
                      });
                      
                      document.getElementById('save-edit')?.addEventListener('click', async () => {
                        try {
                          const updateData: any = {};
                          
                          if (schema.fields) {
                            schema.fields.forEach((field: any) => {
                              if (field.name === 'title' || field.name === 'links') return;
                              
                              const input = document.getElementById(`edit-${field.name}`) as HTMLInputElement | HTMLTextAreaElement | null;
                              if (input && input.value) {
                                if (field.type === 'array') {
                                  try {
                                    updateData[field.name] = JSON.parse(input.value);
                                  } catch {
                                    updateData[field.name] = input.value.split(',').map(s => s.trim());
                                  }
                                } else if (field.type === 'number') {
                                  updateData[field.name] = parseFloat(input.value) || field.default;
                                } else {
                                  updateData[field.name] = input.value;
                                }
                              }
                            });
                          }
                          
                          await patchLocalDevice(did, updateData, text.deviceEditedDrafted);
                          document.body.removeChild(editDialog);
                        } catch (error) {
                          alert(`${text.updateFailed}: ${(error as any).message}`);
                        }
                      });
                    }}
                  >{text.editDeviceTitle}</button>
                </div>
                {/* 新增装置（表单） */}
                <div style={{ marginTop: 10, borderTop: '1px dashed #eee', paddingTop: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 6 }}>{text.newDevice}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input id={`new-device-title-${element.id}`} placeholder={text.title} style={{ fontSize: 12, padding: '2px 6px' }} />
                    <select 
                      id={`new-device-type-${element.id}`} 
                      style={{ fontSize: 12 }} 
                      defaultValue={selectedDeviceTypeByScene[element.id] || ''}
                      onChange={async (e) => {
                        const deviceType = e.target.value;
                        if (deviceType) {
                          await fetchDeviceTypeSchema(deviceType);
                          renderDeviceTypeFields(element.id, deviceType);
                        } else {
                          const fieldsContainer = document.getElementById(`device-fields-${element.id}`);
                          if (fieldsContainer) {
                            fieldsContainer.style.display = 'none';
                            fieldsContainer.innerHTML = '';
                          }
                        }
                      }}
                    >
                      {Object.keys(deviceTypeHints).map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <button
                      style={{ fontSize: 12, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
                      onClick={async () => {
                        const titleInput = document.getElementById(`new-device-title-${element.id}`) as HTMLInputElement | null;
                        const typeSelect = document.getElementById(`new-device-type-${element.id}`) as HTMLSelectElement | null;
                        const title = (titleInput?.value || '').trim();
                        const device_type = (typeSelect?.value || '').trim();
                        if (!title || !device_type) { alert(text.enterTitleAndType); return; }
                        
                        // 获取装置类型的 Schema 以构建完整的装置数据
                        const schema = await fetchDeviceTypeSchema(device_type);
                        const deviceData: any = { 
                          title, 
                          device_type, 
                          links: { scenes: [element.data?.backendId || element.id] } 
                        };
                        
                        // 根据 Schema 添加差异化字段
                        if (schema && schema.fields) {
                          schema.fields.forEach((field: any) => {
                            if (field.name !== 'title' && field.name !== 'links') {
                              const inputId = `new-device-${field.name}-${element.id}`;
                              const input = document.getElementById(inputId) as HTMLInputElement | HTMLSelectElement | null;
                              if (input && input.value) {
                                if (field.type === 'array') {
                                  try {
                                    deviceData[field.name] = JSON.parse(input.value);
                                  } catch {
                                    deviceData[field.name] = input.value.split(',').map(s => s.trim());
                                  }
                                } else if (field.type === 'number') {
                                  deviceData[field.name] = parseFloat(input.value) || field.default;
                                } else {
                                  deviceData[field.name] = input.value;
                                }
                              } else if (field.default !== undefined) {
                                deviceData[field.name] = field.default;
                              }
                            }
                          });
                        }
                        
                        try {
                          onElementCreateFromDraft?.('plot_device', deviceData);
                          if (titleInput) titleInput.value = '';
                          // 清空其他输入字段
                          if (schema && schema.fields) {
                            schema.fields.forEach((field: any) => {
                              if (field.name !== 'title' && field.name !== 'links') {
                                const inputId = `new-device-${field.name}-${element.id}`;
                                const input = document.getElementById(inputId) as HTMLInputElement | HTMLSelectElement | null;
                                if (input) input.value = '';
                              }
                            });
                          }
                        } catch (error) {
                          reportCanvasError(text.createDeviceAction, error);
                        }
                      }}
                    >{text.create}</button>
                  </div>
                  
                  {/* 差异化字段表单 */}
                  <div id={`device-fields-${element.id}`} style={{ marginTop: 8, display: 'none' }}>
                    {/* 动态字段将在这里渲染 */}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {element.type === 'event' && (
          <div style={{ marginTop: 6 }}>
            <select
              value={element.data?.beat || ''}
              onChange={(e) => onSetBeat(element.id, e.target.value)}
              style={{ fontSize: 10 }}
            >
              <option value="">{text.noBeat}</option>
              {beatOrder.map((beat) => (
                <option key={beat} value={beat}>
                  {enumDisplayLabel(beat, locale, 'beat')}
                </option>
              ))}
            </select>
            {/* 选择该事件所属场景，使其出现在场景时间线上 */}
            <div style={{ marginTop: 6 }}>
              <select
                value={element.data?.sceneId || element.data?.scene_id || ''}
                onChange={async (e) => {
                  const sceneId = e.target.value;
                  if (onElementPatch) {
                    await onElementPatch(element.id, { scene_id: sceneId, sceneId }, text.eventAssignedDrafted);
                  } else {
                    element.data = { ...element.data, sceneId, scene_id: sceneId };
                  }
                }}
                style={{ fontSize: 10 }}
              >
                <option value="">{text.unboundScene}</option>
                {sceneElements.map(sc => (
                  <option key={sc.id} value={sc.id}>{sc.name || text.sceneFallback}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {element.type === 'character' && (
          <div style={{ marginTop: 6, width: '100%' }}>
            {/* 简易弧线里程碑UI：新增、绑定到事件或节拍 */}
            <button
              style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
              onClick={async () => {
                const milestoneType = prompt(text.milestoneTypePrompt);
                if (!milestoneType) return;
                const bindMode = prompt(text.bindModePrompt, 'event');
                let payload: any = { title: milestoneType, milestone_type: milestoneType, character_id: element.data?.backendId || element.id };
                if (bindMode === 'beat') {
                  const beat = prompt(text.beatPrompt) || '';
                  if (beat) payload.attached_beat = beat;
                } else if (bindMode === 'event') {
                  const targetEventId = prompt(text.eventIdPrompt);
                  if (targetEventId) payload.attached_event_id = targetEventId;
                }
                const current = Array.isArray(element.data?.milestones) ? element.data.milestones : [];
                const nextMilestones = [...current, payload];
                try {
                  if (onElementPatch) {
                    await onElementPatch(element.id, { milestones: nextMilestones }, text.milestoneDrafted);
                  } else {
                    element.data.milestones = nextMilestones;
                  }
                } catch (error) {
                  reportCanvasError(text.createMilestoneAction, error);
                }
              }}
            >{text.addMilestone}</button>
            <div style={{ marginTop: 6, maxHeight: 60, overflow: 'auto' }}>
              {(element.data?.milestones || []).map((m: any, idx: number) => (
                <div key={idx} style={{ border: '1px solid #f0f0f0', padding: 4, borderRadius: 4, marginBottom: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 'bold' }}>{m.milestone_type || m.title}</div>
                  <div style={{ fontSize: 10, color: '#666' }}>
                    {text.binding}: {m.attached_event_id ? `${text.eventPrefix}:${m.attached_event_id}` : (m.attached_beat ? `${text.beatPrefix}:${m.attached_beat}` : text.none)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // 渲染连接线
  const renderConnection = (connection: any) => {
    const sourceElement = dramaElements.find(el => el.id === connection.sourceId);
    const targetElement = dramaElements.find(el => el.id === connection.targetId);
    
    if (!sourceElement || !targetElement) return null;

    const x1 = sourceElement.position.x + 124;
    const y1 = sourceElement.position.y + 58;
    const x2 = targetElement.position.x + 124;
    const y2 = targetElement.position.y + 58;

    const relationshipColors = {
      loves: '#ff69b4',
      hates: '#ff0000',
      influences: '#4169e1',
      causes: '#32cd32',
      prevents: '#ff8c00',
      transforms: '#9370db'
    };

    const semanticFamily = String(
      connection.semanticFamily || connection.semantic_family || inferRelationshipFamily(connection.relationshipType)
    );
    const color =
      relationshipColors[connection.relationshipType as keyof typeof relationshipColors] ||
      relationshipFamilyColors[semanticFamily] ||
      '#333';

    return (
      <svg
        key={connection.id}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1
        }}
      >
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={color}
          strokeWidth={Math.max(2, Number(connection.strength) || 2)}
          strokeDasharray={connection.relationshipType === 'prevents' ? '5,5' : 'none'}
        />
        {/* 箭头 */}
        <polygon
          points={`${x2-5},${y2-5} ${x2+5},${y2} ${x2-5},${y2+5}`}
          fill={color}
        />
        {/* 关系标签 */}
        <text
          x={(x1 + x2) / 2}
          y={(y1 + y2) / 2 - 5}
          textAnchor="middle"
          fontSize="10"
          fill={color}
          style={{ background: 'white', padding: '2px' }}
        >
          {connection.relationshipType}
        </text>
      </svg>
    );
  };

  const handleElementClick = (elementId: string) => {
    if (isConnecting) {
      if (connectionSource === null) {
        setConnectionSource(elementId);
      } else if (connectionSource !== elementId) {
        const sourceEl = dramaElements.find(el => el.id === connectionSource);
        const targetEl = dramaElements.find(el => el.id === elementId);
        const srcType = sourceEl?.type;
        const tgtType = targetEl?.type;

        const ruleKey = `${srcType}-${tgtType}`;
        const reverseRuleKey = `${tgtType}-${srcType}`;
        const rule = connectionPairRules[ruleKey] || connectionPairRules[reverseRuleKey];

        if (!rule || !rule.allowed) {
          let suggestion = '';
          if (srcType === 'device' || tgtType === 'device' || srcType === 'plot_device' || tgtType === 'plot_device') {
            suggestion = text.deviceBindingSuggestion;
          } else if (srcType === 'event' || tgtType === 'event') {
            suggestion = text.eventBindingSuggestion;
          } else {
            suggestion = text.unsupportedPairSuggestion;
          }

          alert(`${text.unsupportedConnection}: ${srcType} -> ${tgtType}\n\n${text.suggestion}: ${suggestion}`);
          setConnectionSource(null);
          setIsConnecting(false);
          return;
        }

        const ruleRelationshipTypes = Array.isArray(rule.relationshipTypes) ? rule.relationshipTypes : [];
        const relationshipTypeOptions = ruleRelationshipTypes.join(', ');
        const relationshipType = window
          .prompt(
            `${text.relationshipPrompt} (${rule.description}).\nSuggested: ${relationshipTypeOptions}\n${text.customLabelHint}`,
            ruleRelationshipTypes[0] || 'influences'
          )
          ?.trim();

        if (!relationshipType) {
          setConnectionSource(null);
          setIsConnecting(false);
          return;
        }

        const defaultSemanticFamily = inferRelationshipFamily(relationshipType, rule.semanticFamily || 'influence');
        let semanticFamily = defaultSemanticFamily;
        if (!ruleRelationshipTypes.includes(relationshipType)) {
          const familyOptions = relationshipFamilies.length ? relationshipFamilies : ['influence', 'custom'];
          const customSemanticFamily = window
            .prompt(
              `${text.semanticFamilyPrompt} for "${relationshipType}".\nOptions: ${familyOptions.join(', ')}`,
              defaultSemanticFamily
            )
            ?.trim()
            .toLowerCase();
          if (customSemanticFamily && familyOptions.includes(customSemanticFamily)) {
            semanticFamily = customSemanticFamily;
          }
        }

        onConnectionCreate(connectionSource, elementId, relationshipType, { semanticFamily });
        setConnectionSource(null);
        setIsConnecting(false);
      }
      return;
    }

    onElementSelect?.(elementId);
  };

  const handleElementEdit = (element: any) => {
    onElementSelect?.(element.id);
    if (onElementEdit) {
      onElementEdit(element);
    } else {
      // 降级方案：使用 prompt
      const newName = prompt(text.renamePrompt, element.name);
      if (newName && newName !== element.name) {
        console.log(`${text.renameLog}:`, element.id, newName);
      }
    }
  };

  const startConnection = () => {
    setIsConnecting(true);
    setConnectionSource(null);
  };

  const cancelConnection = () => {
    setIsConnecting(false);
    setConnectionSource(null);
  };

  const setCanvasZoom = (nextZoom: number) => setZoom(clampZoom(nextZoom));

  const fitCanvas = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const fitted = fitViewport(canvasBounds, {
      width: viewport.clientWidth,
      height: viewport.clientHeight,
    });
    setZoom(fitted.zoom);
    viewport.scrollTo({ left: fitted.scrollLeft, top: fitted.scrollTop, behavior: 'smooth' });
  };

  return (
    <div
      ref={viewportRef}
      data-testid="drama-canvas-viewport"
      onWheel={(event) => {
        if (!event.ctrlKey && !event.metaKey) return;
        event.preventDefault();
        setCanvasZoom(zoom + (event.deltaY < 0 ? 0.1 : -0.1));
      }}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'auto',
        background: '#fef9e7',
      fontFamily: "'Noto Serif SC', '思源宋体', serif"
    }}>
      {/* 由当前项目结构推导的泳道背景；不预设三幕 */}
      <div
        data-testid="drama-canvas-content"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: canvasBounds.width,
          height: canvasBounds.height,
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      >
        {lanePlan.lanes.map((act, idx) => {
          const lane = {
            key: act,
            label: lanePlan.mode === 'structure' && /^act_/i.test(act)
              ? enumDisplayLabel(act, locale, 'act')
              : act,
            color: idx % 2 === 0 ? '#ffffff' : '#fef9e7',
          };
          const laneWidth = 100 / Math.max(1, lanePlan.lanes.length);
          return (
          <div key={lane.key} style={{ 
            position:'absolute', 
            top: 0, 
            left: `${idx * laneWidth}%`, 
            width: `${laneWidth}%`, 
            height:'100%', 
            background: lane.color, 
            borderRight: idx < lanePlan.lanes.length - 1 ? '3px solid #1a1a1a' : 'none'
          }}>
            {/* 电影胶片穿孔效果 */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: 'repeating-linear-gradient(90deg, #1a1a1a 0px, #1a1a1a 8px, transparent 8px, transparent 16px)'
            }} />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '6px',
              background: 'repeating-linear-gradient(90deg, #1a1a1a 0px, #1a1a1a 8px, transparent 8px, transparent 16px)'
            }} />
            <div style={{ 
              position:'absolute', 
              top: 16, 
              left: 16, 
              fontSize: 14, 
              color: '#1a1a1a',
              fontFamily: "'Noto Serif SC', '思源宋体', serif",
              fontWeight: 700,
              letterSpacing: '0.5px',
              zIndex: 1
            }}>{lane.label}</div>
          </div>
        )})}
      </div>
      {/* 连接模式提示 */}
      {isConnecting && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#ffffff',
          color: '#1a1a1a',
          padding: '24px',
          borderRadius: '20px',
          border: '3px solid #1a1a1a',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          zIndex: 1000,
          textAlign: 'center',
          fontFamily: "'Noto Serif SC', '思源宋体', serif"
        }}>
          <p style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>{text.connectionModeActive}</p>
          <p style={{ fontSize: '14px', marginBottom: '16px' }}>{text.connectionModeHint}</p>
          <button 
            onClick={cancelConnection} 
            style={{ 
              marginTop: '10px',
              padding: '8px 16px',
              background: '#ffffff',
              color: '#1a1a1a',
              border: '2px solid #1a1a1a',
              borderRadius: '12px',
              cursor: 'pointer',
              fontFamily: "'Noto Serif SC', '思源宋体', serif",
              fontWeight: 500,
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#fef9e7';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {text.cancelConnection}
          </button>
        </div>
      )}

      {/* 戏剧元素覆盖层 */}
      <div
        data-testid="drama-canvas-elements"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: canvasBounds.width,
          height: canvasBounds.height,
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          pointerEvents: 'auto',
          zIndex: 2
        }}
        onMouseMove={(e) => {
          if (!draggingElement && pendingDrag) {
            const deltaX = e.clientX - pendingDrag.startX;
            const deltaY = e.clientY - pendingDrag.startY;
            if (Math.hypot(deltaX, deltaY) >= DRAG_THRESHOLD_PX) {
              setDraggingElement(pendingDrag.elementId);
              setDragOffset({ x: pendingDrag.offsetX, y: pendingDrag.offsetY });
            }
          }

          if (draggingElement && dragOffset && onElementPositionUpdate) {
            const container = e.currentTarget;
            const containerRect = container.getBoundingClientRect();
            const newX = (e.clientX - containerRect.left) / zoom - dragOffset.x;
            const newY = (e.clientY - containerRect.top) / zoom - dragOffset.y;
            onElementPositionUpdate(draggingElement, { x: Math.max(0, newX), y: Math.max(0, newY) });
          }
        }}
        onMouseUp={() => {
          setPendingDrag(null);
          if (draggingElement) {
            setDraggingElement(null);
            setDragOffset(null);
            setHoveredElement(null);
          }
        }}
        onMouseLeave={() => {
          setPendingDrag(null);
          if (draggingElement) {
            setDraggingElement(null);
            setDragOffset(null);
            setHoveredElement(null);
          }
        }}
      >
        {dramaElements.map(renderDramaElement)}
        {displayConnections.map(renderConnection)}
      </div>

      <div
        aria-label={locale === 'zh' ? '画布缩放' : 'Canvas zoom'}
        style={{
          position: 'sticky',
          top: 12,
          left: '100%',
          zIndex: 2100,
          display: 'flex',
          alignItems: 'center',
          width: 'max-content',
          margin: '12px 12px 0 auto',
          gap: 6,
          padding: 6,
          border: '1px solid rgba(26,26,26,.22)',
          borderRadius: 12,
          background: 'rgba(255,255,255,.94)',
          boxShadow: '0 6px 18px rgba(0,0,0,.1)',
        }}
      >
        <button aria-label={locale === 'zh' ? '缩小画布' : 'Zoom out'} onClick={() => setCanvasZoom(zoom - 0.1)}>−</button>
        <button aria-label={locale === 'zh' ? '重置为 100%' : 'Reset to 100%'} onClick={() => setCanvasZoom(1)}>{Math.round(zoom * 100)}%</button>
        <button aria-label={locale === 'zh' ? '放大画布' : 'Zoom in'} onClick={() => setCanvasZoom(zoom + 0.1)}>＋</button>
        <button aria-label={locale === 'zh' ? '适配全图' : 'Fit canvas'} onClick={fitCanvas}>{locale === 'zh' ? '适配' : 'Fit'}</button>
      </div>

      {/* 连接控制按钮 */}
      <div style={{
        position: 'absolute',
        bottom: connectionControlsBottom,
        right: isMobileLayout ? '12px' : '20px',
        zIndex: 905,
        display: 'flex',
        gap: '10px',
        flexDirection: isMobileLayout ? 'column' : 'row',
        alignItems: 'flex-end'
      }}>
        <button
          onClick={startConnection}
          style={{
            padding: '10px 20px',
            background: isConnecting ? '#fef9e7' : '#ffffff',
            color: '#1a1a1a',
            border: '2px solid #1a1a1a',
            borderRadius: '16px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 700,
            fontFamily: "'Noto Serif SC', '思源宋体', serif",
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#fef9e7';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.12)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = isConnecting ? '#fef9e7' : '#ffffff';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {isConnecting ? text.connectionMode : text.startConnection}
        </button>
        <button
          onClick={() => setShowConnectionHelp(!showConnectionHelp)}
          style={{
            padding: '10px 20px',
            background: '#ffffff',
            color: '#1a1a1a',
            border: '2px solid #1a1a1a',
            borderRadius: '16px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 700,
            fontFamily: "'Noto Serif SC', '思源宋体', serif",
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#fef9e7';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.12)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = '#ffffff';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {text.connectionHelp}
        </button>
      </div>

      {/* 连线帮助面板 */}
      {showConnectionHelp && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(16, 10, 2, 0.32)',
          zIndex: 2300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobileLayout ? '12px' : '24px'
        }}>
          <div style={{
            background: '#ffffff',
            border: '3px solid #1a1a1a',
            borderRadius: '20px',
            padding: '24px',
            minWidth: isMobileLayout ? '100%' : '500px',
            width: isMobileLayout ? '100%' : 'auto',
            maxWidth: 'min(720px, 100%)',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            fontFamily: "'Noto Serif SC', '思源宋体', serif"
          }}>
          <h3 style={{ 
            margin: '0 0 15px 0', 
            fontSize: '20px', 
            color: '#1a1a1a',
            fontWeight: 700
          }}>{text.connectionRulesTitle}</h3>
          <div style={{ marginBottom: '15px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>{text.supportedConnectionTypes}</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', lineHeight: '1.6' }}>
              <li><strong>Character ↔ Character</strong>: loves, hates, trusts, betrays, influences, competes_with, allies_with, mentors</li>
              <li><strong>Character ↔ Scene</strong>: participates_in, observes, influences, causes</li>
              <li><strong>Scene ↔ Event</strong>: contains, triggers, caused_by, enables</li>
              <li><strong>Device ↔ Scene</strong>: setup_in, payoff_in, affects, binds_to</li>
              <li><strong>Device ↔ Character</strong>: influences, reveals_about, constrains, empowers</li>
              <li><strong>Event ↔ Event</strong>: causes, prevents, enables, follows, parallels</li>
            </ul>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>{text.instructionsTitle}</h4>
            <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', lineHeight: '1.6' }}>
              {text.instructions.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>{text.notesTitle}</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', lineHeight: '1.6' }}>
              {text.notes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div style={{ textAlign: 'right' }}>
            <button
              onClick={() => setShowConnectionHelp(false)}
              style={{
                padding: '8px 16px',
                background: '#ffffff',
                color: '#1a1a1a',
                border: '2px solid #1a1a1a',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '12px',
                fontFamily: "'Noto Serif SC', '思源宋体', serif",
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#fef9e7';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                {text.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DramaCanvas;
